import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { useFeatures } from '../../../settings/hooks/useSettingsState';
import TodayWidget from '../../../classroom/components/TodayWidget';
import LearningProgressOverview from './LearningProgressOverview';
import CoursioNetworkRecommendations from './CoursioNetworkRecommendations';
import LearningStreakWidget from './LearningStreakWidget';
import DiplomasWidget from './DiplomasWidget';
import NotificationsWidget from './NotificationsWidget';
import UpcomingDeadlinesWidget from './UpcomingDeadlinesWidget';
import MyCoursesList from '../MyCoursesList';
import ListCourses from '../../../../components/ListCourses';
import { MOCK_COURSIO_NETWORK_COURSES, MOCK_CERTIFICATES, MOCK_LEARNING_STREAK, MOCK_UPCOMING_DEADLINES, MOCK_NOTIFICATIONS } from './mockStudentData';
import NetworkSuggestions from '../../../../components/CoursioNetwork';
import NetworkUpsells from '../../../../components/CoursioNetwork/NetworkUpsells';
import WelcomeMessage from '../WelcomeMessage';
import WelcomeRow from '../WelcomeRow';
import { media } from '../../../../utils/media';
import PageSection from '../../../../ui/Page/PageSection';

const DashboardContainer = styled(Flex)`
  width: 100%;
  padding: 0 0 40px;

  ${media.lessThan('tablet')} {
    padding: 0 0 32px;
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 24px;
  width: 100%;
  margin-bottom: 32px;

  ${media.lessThan('tablet')} {
    grid-template-columns: 1fr;
  }
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${theme.colors.NEU_200};
  margin: 40px 0;
`;

/**
 * StudentDashboardContent Component
 *
 * Student dashboard featuring:
 * - Learning progress overview with statistics
 * - Ongoing and completed courses
 * - Learning streak tracking
 * - Diplomas and certificates
 * - Recent notifications and activity
 * - Upcoming deadlines
 * - Coursio Network course recommendations
 *
 * Combines real course data with mock data for new features.
 */
const StudentDashboardContent = ({ dashboardData = [], isLoading = false }) => {
  const features = useFeatures();

  // Filter courses for student view (exclude courses they can edit)
  const myCourses = dashboardData.filter((course) => !course.canEdit);
  const ongoingCourses = myCourses.filter((course) => course.progress > 0 && course.progress < 1);

  return (
    <DashboardContainer direction='column' align='flex-start' gap='20'>
      <PageSection>
        {/* Welcome + Learning Progress Overview */}
        <WelcomeRow>
          <WelcomeMessage role={'reader'} />
          <LearningProgressOverview courses={myCourses} />
        </WelcomeRow>

        {/* Classroom: Upcoming Sessions (only when classroom feature is enabled) */}
        {features?.classroom && (
          <Flex width='100%'>
            <TodayWidget />
          </Flex>
        )}
      </PageSection>
      {/* Ongoing Courses */}
      {isLoading && !myCourses.length ? (
        <PageSection>
          <ListCourses isLoading={true} />
        </PageSection>
      ) : (
        <>
          {ongoingCourses.length > 0 && (
            <Flex width='100%' direction='column' align='flex-start' gap='20' style={{ marginBottom: '32px' }}>
              <MyCoursesList courses={myCourses} />
            </Flex>
          )}

          {/* No Courses Message */}
          {!myCourses.length && (
            <PageSection>
              <Flex
                direction='column'
                align='center'
                gap='20'
                style={{
                  // background: theme.colors.NEU_0,
                  borderRadius: '16px',
                  padding: '48px 24px',
                  border: `1px solid ${theme.colors.NEU_200}`,
                  marginBottom: '32px',
                }}
              >
                <Typo.ContentSmallHeader color={theme.colors.NEU_PRIMARY}>No courses yet</Typo.ContentSmallHeader>
                <Typo.Text $textAlign='center' color={theme.colors.NEU_600}>
                  Start your learning journey by enrolling in courses or check with your instructor for course invitations.
                </Typo.Text>
              </Flex>
            </PageSection>
          )}
        </>
      )}
      <PageSection>
        <TwoColumnLayout>
          {/* Upcoming Deadlines - Full Width */}
          {MOCK_UPCOMING_DEADLINES.length > 0 && <UpcomingDeadlinesWidget deadlines={MOCK_UPCOMING_DEADLINES} />}
          {/* Notifications Widget - Full Width */}
          {MOCK_NOTIFICATIONS.length > 0 && <NotificationsWidget notifications={MOCK_NOTIFICATIONS} />}
        </TwoColumnLayout>
      </PageSection>
      {/* Two Column Layout: Streak + Diplomas */}
      <PageSection>
        <TwoColumnLayout>
          <LearningStreakWidget streakData={MOCK_LEARNING_STREAK} />
          <DiplomasWidget certificates={MOCK_CERTIFICATES} />
        </TwoColumnLayout>
      </PageSection>
      {features.coursioNetwork ? (
        <>
          <NetworkUpsells />
          <NetworkSuggestions />
        </>
      ) : (
        <PageSection>
          <CoursioNetworkRecommendations recommendations={MOCK_COURSIO_NETWORK_COURSES} />
        </PageSection>
      )}
    </DashboardContainer>
  );
};

export default StudentDashboardContent;
